import { Link, Text, chakra, Tr, Td } from '@chakra-ui/react';
import React from 'react';

import { useI18n } from 'lib/i18n/context';
import { getSocketNoticeKey, type SocketNoticeType } from 'lib/i18n/socketNotice';
import Skeleton from 'ui/shared/chakra/Skeleton';
import ScanningNotice, { AnimatedDots } from 'ui/shared/ScanningNotice';

interface InjectedProps {
  content: React.ReactNode;
}

interface Props {
  type?: SocketNoticeType;
  children?: (props: InjectedProps) => React.JSX.Element;
  className?: string;
  url: string;
  alert?: string;
  num?: number;
  isLoading?: boolean;
}

const SocketNewItemsNotice = chakra(({ children, className, url, num, alert, type = 'transaction', isLoading }: Props) => {
  const { t } = useI18n();

  if (!isLoading && !num && !alert) {
    return null;
  }

  const alertContent = (() => {
    if (alert) {
      return alert;
    }

    if (!num) {
      // strip the trailing dots/ellipsis from the localized string; they are rendered as an animation instead
      const scanningText = t(getSocketNoticeKey(type)).replace(/[.…\s]+$/, '');
      return <>{ scanningText }<AnimatedDots/></>;
    }

    return (
      <>
        <Link href={ url }>{ t(getSocketNoticeKey(type, num), { count: num.toLocaleString() }) }</Link>
        <Text whiteSpace="pre"> { t('common.socketNotice.haveComeIn') }</Text>
      </>
    );
  })();

  const content = isLoading ? (
    <Skeleton className={ className } h="32px"/>
  ) : (
    <ScanningNotice className={ className }>{ alertContent }</ScanningNotice>
  );

  return children ? children({ content }) : content;
});

export default SocketNewItemsNotice;

export const Desktop = ({ ...props }: Props) => {
  return (
    <SocketNewItemsNotice
      borderRadius={ props.isLoading ? 'sm' : 0 }
      h={ props.isLoading ? 5 : 'auto' }
      maxW={ props.isLoading ? '215px' : undefined }
      w="100%"
      mx={ props.isLoading ? 4 : 0 }
      my={ props.isLoading ? '6px' : 0 }
      { ...props }
    >
      { ({ content }) => <Tr><Td colSpan={ 100 } p={ 0 } _first={{ p: 0 }} _last={{ p: 0 }}>{ content }</Td></Tr> }
    </SocketNewItemsNotice>
  );
};

export const Mobile = ({ ...props }: Props) => {
  return (
    <SocketNewItemsNotice
      borderBottomRadius={ 0 }
      { ...props }
    />
  );
};
